import { MouseEvent, useState } from 'react';

import styled from 'styled-components';

import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { TextFieldProps, TextField } from '@components/text-field';
import { transitionSlow } from '@utils/transitions';
import { Transient } from '@utils/types';

export type PasswordFieldProps = TextFieldProps & {
  hideVisibilityOnEmpty?: boolean;
  /** Controls whether the password is shown; leave unset to let the field keep track. */
  isPasswordVisible?: boolean;
  onPasswordVisibilityChange?: (isPasswordVisible: boolean) => void;
};

// Pressing the eye would otherwise move focus off the field and back again.
const keepFocusInField = (event: MouseEvent) => {
  event.preventDefault();
};

export function PasswordField({
  hideVisibilityOnEmpty = false,
  isPasswordVisible,
  onPasswordVisibilityChange,
  ...rest
}: PasswordFieldProps) {
  const [isShownLocally, setIsShownLocally] = useState(false);
  const show = isPasswordVisible ?? isShownLocally;

  const setShow = (isShown: boolean) => {
    setIsShownLocally(isShown);
    onPasswordVisibilityChange?.(isShown);
  };

  return (
    <Container $hideVisibilityOnEmpty={hideVisibilityOnEmpty}>
      <TextField
        {...rest}
        type={show ? 'text' : 'password'}
        iconRight={
          show ? (
            <IconVisibilityShown
              onMouseDown={keepFocusInField}
              onClick={() => {
                setShow(false);
              }}
            />
          ) : (
            <IconVisibilityHidden
              onMouseDown={keepFocusInField}
              onClick={() => {
                setShow(true);
              }}
            />
          )
        }
      />
    </Container>
  );
}

const Container = styled.div<Transient<PasswordFieldProps, 'hideVisibilityOnEmpty'>>(
  ({ $hideVisibilityOnEmpty }) => {
    return {
      svg: {
        transition: transitionSlow('opacity'),
        opacity: 1,
      },
      ...($hideVisibilityOnEmpty && {
        'input:placeholder-shown + svg': {
          opacity: 0,
        },
      }),
    };
  },
);
